
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConversationalInterface } from '@/components/analytics/ConversationalInterface';
import { MessageSquare, Mic, BarChart3, Brain } from 'lucide-react';

export default function ConversationalAnalytics() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Conversational Analytics</h1>
          <p className="text-gray-600 mt-2">
            Ask natural language questions about player performance, training plans, and insights
          </p>
        </div>
      </div>

      {/* Feature Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium">Natural Language</h3>
                <p className="text-sm text-gray-600">Ask questions in plain English</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium">Live Data</h3>
                <p className="text-sm text-gray-600">Real-time analytics insights</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium">AI Powered</h3>
                <p className="text-sm text-gray-600">Intelligent responses</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Mic className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-medium">Voice Input</h3>
                <p className="text-sm text-gray-600">Hands-free coaching</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Example queries */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Example Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-blue-600 mb-2">Performance Analysis</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• "How is Tom's batting improving?"</li>
                <li>• "Show me the team's strike rate this month"</li>
                <li>• "What's my average exit velocity?"</li>
                <li>• "How many boundaries did Sarah hit yesterday?"</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-green-600 mb-2">Training Plans</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• "What should I focus on in training?"</li>
                <li>• "Create a drill plan for power hitting"</li>
                <li>• "What exercises improve timing?"</li>
                <li>• "Plan tomorrow's practice session"</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-purple-600 mb-2">Comparisons</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• "Compare Alex vs Jamie's performance"</li>
                <li>• "Who are our top 3 performers?"</li>
                <li>• "How do I rank against my teammates?"</li>
                <li>• "Show the best players this week"</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-orange-600 mb-2">Predictions</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                <li>• "When will I reach my target average?"</li>
                <li>• "Predict my performance next month"</li>
                <li>• "What's my expected improvement rate?"</li>
                <li>• "Will the team meet their goals?"</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Conversational Interface */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Analytics Assistant
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ConversationalInterface className="min-h-[600px]" />
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tips for Better Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Be Specific</h4>
              <p className="text-sm text-blue-700">
                Include player names, time ranges, and specific metrics for more accurate results.
              </p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Use Natural Language</h4>
              <p className="text-sm text-green-700">
                Ask questions as you would to a human coach - no need for technical commands.
              </p>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">Follow Up</h4>
              <p className="text-sm text-purple-700">
                Ask follow-up questions to dive deeper into the analysis and get more insights.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}